import { Email } from "../models/entities/email.model";

/**
 * KAN-1176 WORKAROUND — primary-email normalisation.
 *
 * Upstream bug: a survey submitted with an email that differs from the one on
 * file can leave a participant with *zero* or *multiple* emails flagged
 * `isPrimary`. Downstream, an ambiguous primary is what duplicates a
 * participant into two rows on the participant tab (KAN-1176).
 *
 * Until the survey-ingestion path guarantees exactly one primary email, these
 * helpers defensively resolve / normalise the primary on the front end.
 *
 * SELF-HEALING: once upstream guarantees a single primary, both helpers become
 * no-ops — `resolvePrimaryEmailAddress` returns that primary (the fallback is
 * never taken) and `ensureSinglePrimaryEmail` finds it (nothing is mutated).
 * They are therefore safe to leave in place, and safe to delete once KAN-1176's
 * root cause is fixed. To remove the workaround entirely, delete this file and
 * its call sites — `grep -rn KAN-1176 src` finds them.
 */

/** Fields we need to reason about which email is primary. */
type PrimaryCandidate = Pick<Email, "emailAddress" | "isPrimary">;

/**
 * The participant's primary email address, falling back to the first email when
 * none is flagged primary. Returns `null` when there are no emails.
 */
export const resolvePrimaryEmailAddress = (
	emails: PrimaryCandidate[] | null | undefined
): string | null => {
	if (!emails?.length) return null;
	const primary = emails.find((email) => email.isPrimary) ?? emails[0];
	return primary.emailAddress;
};

/**
 * Ensure exactly one active (non-deleted) email is flagged primary, mutating the
 * array in place. If none is primary, the first active email is promoted so the
 * backend never receives an ambiguous payload. No-op when a primary already
 * exists or there are no active emails.
 */
export const ensureSinglePrimaryEmail = (emails: Array<Partial<Email>>): void => {
	const active = emails.filter((email) => !email.deleted);
	if (active.length > 0 && !active.some((email) => email.isPrimary)) {
		active[0].isPrimary = true;
	}
};
